import React from 'react';

import {
  MarqueDto,
  MarqueDtoCategorieEnum,
  MarqueDtoTypeEnum,
  MarqueRequestDto,
  MarqueRequestDtoCategorieEnum,
  MarqueRequestDtoTypeEnum,
  ReferentielSearchResultDtoMarqueDto,
} from 'lib_api/lib/api/gen';

import {
  FetchDataResult,
  TableHeaderFunctionWithoutSort,
  TablePagination,
  TableSort,
} from 'components/WrappedComponents/Table/types';
import { useApi } from 'hooks/ApiStoreContext';

import DashboardHeader from './DashboardHeader/DashboardHeader';
import { FilterDashboardMarque } from './DashboardHeader/types';
import { MarqueDtoFormValues, MarqueSort } from './types';

export const buildHeader: TableHeaderFunctionWithoutSort<
  MarqueDto,
  FilterDashboardMarque
> = (fetchedData, _selectedData, filter, setFilter) => (
  <DashboardHeader
    totalCount={fetchedData !== undefined ? fetchedData.total : 0}
    filter={filter}
    setFilter={setFilter}
  />
);

/**
 * Hook sent to generic search hook to send search request and convert returned result to readable data
 */
export function useSearchMarque(
  filters: FilterDashboardMarque,
  sorts: TableSort<MarqueSort>,
  pagination: TablePagination,
): [
  () => Promise<ReferentielSearchResultDtoMarqueDto>,
  (result: ReferentielSearchResultDtoMarqueDto) => FetchDataResult<MarqueDto>,
] {
  const controller = useApi().ReferentielControllerApi;

  return [
    () => {
      return controller.searchMarqueUsingSpecsUsingGET({
        filters: {
          ...filters,
          types: filters?.type ? [filters.type] : [],
          page: pagination.page,
          pageSize: pagination.pageSize,
        },
        sorts: {
          categorie: sorts?.CATEGORIE,
          libelle: sorts?.LIBELLE,
          type: sorts?.TYPE,
        },
      });
    },
    result => {
      return {
        data: result.results.referenceDtoList,
        total: result.total,
      };
    },
  ];
}

export function buildDefaultUpdateFormValues(
  marque: MarqueDto,
  marqueExacte: MarqueDto | null,
): MarqueDtoFormValues {
  return {
    libelle: marque.libelle,
    categorie: marque.categorie ?? undefined,
    type: marque.type ?? undefined,
    marqueExacte: {
      value: marqueExacte?.id,
      label: marqueExacte?.libelle,
      option: marqueExacte ?? undefined,
    },
  };
}

export type CategorieEnumMapping = {
  [key in MarqueDtoCategorieEnum]: MarqueRequestDtoCategorieEnum;
};
export type TypeEnumMapping = {
  [key in MarqueDtoTypeEnum]: MarqueRequestDtoTypeEnum;
};

export const categorieEnumMapping: CategorieEnumMapping = {
  A_CATEGORISER: MarqueRequestDtoCategorieEnum.A_CATEGORISER,
  PREMIUM: MarqueRequestDtoCategorieEnum.PREMIUM,
  NON_VALORISABLE: MarqueRequestDtoCategorieEnum.NON_VALORISABLE,
};
export const typeEnumMapping: TypeEnumMapping = {
  INCONNUE: MarqueRequestDtoTypeEnum.INCONNUE,
  APPROXIMATIVE: MarqueRequestDtoTypeEnum.APPROXIMATIVE,
  EXACTE: MarqueRequestDtoTypeEnum.EXACTE,
};

export function convertMarqueToUpdateMarqueRequestDto(
  formValues: MarqueDtoFormValues,
): MarqueRequestDto {
  return {
    categorie: formValues.categorie
      ? categorieEnumMapping[formValues.categorie]
      : null,
    type: formValues.type ? typeEnumMapping[formValues.type] : null,
    marqueExacteId:
      formValues.type === MarqueDtoTypeEnum.APPROXIMATIVE &&
      formValues?.marqueExacte?.option?.id
        ? formValues.marqueExacte?.option?.id
        : null,
  };
}
